import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Calculator, 
  FileText, 
  TrendingUp, 
  Shield, 
  ChevronRight,
  Building2,
  Users,
  BarChart3
} from "lucide-react";

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-white rounded-lg px-3 py-1.5">
              <img src="/evolutt_logo_white.png" alt="Evolutt" className="h-8 w-auto" />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" data-testid="login-btn">
                Entrar
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-primary text-primary-foreground hover:bg-slate-800" data-testid="register-btn">
                Criar Conta
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6" data-testid="hero-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600/10 border border-brand-600/30 rounded-full">
              <span className="w-2 h-2 bg-brand-600 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-foreground">Legislação Portugal 2026</span>
            </div>
            
            <h1 className="font-manrope font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground leading-tight">
              Simulador Financeiro
              <br />
              <span className="block mt-4">para <span className="text-white bg-brand-600 px-3 py-1 rounded-lg">Evolutt</span></span>
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-lg leading-relaxed">
              Compare recibos verdes, B2B e contrato de trabalho. Calcule margens, 
              custos salariais e optimize a sua estratégia de contratação.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-brand-600 text-white hover:bg-brand-700 font-bold px-8" data-testid="cta-start-btn">
                  Começar Simulação
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-2" data-testid="cta-login-btn">
                  Já tenho conta
                </Button>
              </Link>
            </div>
            
            <div className="flex items-center gap-8 pt-4">
              <div className="text-center">
                <p className="font-mono font-semibold text-2xl text-foreground tabular-nums">3</p>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Regimes</p>
              </div>
              <div className="w-px h-12 bg-border"></div>
              <div className="text-center">
                <p className="font-mono font-semibold text-2xl text-foreground tabular-nums">IRS 2026</p>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Tabelas</p>
              </div>
              <div className="w-px h-12 bg-border"></div>
              <div className="text-center">
                <p className="font-mono font-semibold text-2xl text-foreground tabular-nums">100%</p>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Automático</p>
              </div>
            </div>
          </div>
          
          <div className="relative animate-slide-in" style={{ animationDelay: "200ms" }}>
            <div className="absolute inset-0 bg-brand-600/20 rounded-3xl blur-3xl"></div>
            <img
              src="https://images.unsplash.com/photo-1758518727707-b023e285b709?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjBtZWV0aW5nJTIwZmluYW5jZXxlbnwwfHx8fDE3NzI5OTU1NTF8MA&ixlib=rb-4.1.0&q=85"
              alt="Professional team meeting"
              className="relative rounded-2xl shadow-2xl object-cover w-full h-[400px] lg:h-[500px]"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-secondary/30" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-4">Funcionalidades</p>
            <h2 className="font-manrope font-extrabold text-3xl sm:text-4xl tracking-tight text-foreground">
              Tudo o que precisa para decidir
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="bg-white border border-border/60 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200 hover:-translate-y-1" data-testid="feature-card-1">
              <CardContent className="p-6 space-y-4">
                <div className="w-12 h-12 rounded-xl bg-brand-600 flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-manrope font-bold text-lg text-foreground">Simulador Completo</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Compare recibos verdes, B2B e contrato de trabalho com todos os encargos incluídos.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border border-border/60 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200 hover:-translate-y-1" data-testid="feature-card-2">
              <CardContent className="p-6 space-y-4">
                <div className="w-12 h-12 rounded-xl bg-brand-600 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-manrope font-bold text-lg text-foreground">Tabelas IRS Atualizadas</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Tabelas IRS 2026 pré-carregadas e editáveis via backoffice administrativo.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border border-border/60 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200 hover:-translate-y-1" data-testid="feature-card-3">
              <CardContent className="p-6 space-y-4">
                <div className="w-12 h-12 rounded-xl bg-brand-600 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-manrope font-bold text-lg text-foreground">Cálculo de Margens</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Configure margens de outsourcing e veja o impacto no custo total do cliente.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border border-border/60 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200 hover:-translate-y-1" data-testid="feature-card-4">
              <CardContent className="p-6 space-y-4">
                <div className="w-12 h-12 rounded-xl bg-brand-600 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-manrope font-bold text-lg text-foreground">Segurança Social</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Cálculo automático das contribuições para cada regime contratual.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border border-border/60 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200 hover:-translate-y-1" data-testid="feature-card-5">
              <CardContent className="p-6 space-y-4">
                <div className="w-12 h-12 rounded-xl bg-brand-600 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-manrope font-bold text-lg text-foreground">Custos Empresariais</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Gestão de custos operacionais: seguros, fundos de compensação, contabilidade.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border border-border/60 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200 hover:-translate-y-1" data-testid="feature-card-6">
              <CardContent className="p-6 space-y-4">
                <div className="w-12 h-12 rounded-xl bg-brand-600 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-manrope font-bold text-lg text-foreground">Backoffice Admin</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Painel administrativo para gerir tabelas, custos e configurações.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6" data-testid="cta-section">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-brand-600 text-white overflow-hidden">
            <CardContent className="p-8 md:p-12 text-center space-y-6">
              <div className="flex justify-center gap-4">
                <Users className="w-12 h-12 text-white" />
              </div>
              <h2 className="font-manrope font-extrabold text-2xl md:text-3xl tracking-tight">
                Pronto para otimizar as suas contratações?
              </h2>
              <p className="text-white/80 max-w-lg mx-auto">
                Crie uma conta gratuita e comece a simular diferentes cenários para 
                encontrar a melhor solução para a sua empresa ou carreira.
              </p>
              <Link to="/register">
                <Button size="lg" className="bg-white text-brand-600 hover:bg-brand-50 font-bold px-8" data-testid="cta-register-btn">
                  Criar Conta Gratuita
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border" data-testid="footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="bg-[var(--brand-primary)] rounded-md px-2 py-1">
              <img src="/evolutt_logo_white.png" alt="Evolutt" className="h-6 w-auto" />
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2026 Evolutt. Simulador Financeiro para Outsourcing em Portugal.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
